import React, { useState } from "react";
import "@/app/pages/companyProfile/addedBy/MediaContainer.scss";
import { CompanyEnhancedFields } from "@/app/pages/companyProfile/addedBy/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronRight,
  faChevronLeft,
  faCircleXmark,
  faCirclePlay,
} from "@fortawesome/free-solid-svg-icons";

interface MediaOverlayProps {
  media: Array<{ url: string; type: string; thumbnailUrl?: string | null; originalUrl?: string; }>;
  currentIndex: number;
  onClose: () => void;
}

const MediaOverlay: React.FC<MediaOverlayProps> = ({
  media,
  currentIndex,
  onClose,
}) => {
  const [current, setCurrent] = useState(currentIndex);

  const handlePrevious = () => {
    setCurrent((prev) => (prev > 0 ? prev - 1 : media.length - 1));
  };

  const handleNext = () => {
    setCurrent((prev) => (prev < media.length - 1 ? prev + 1 : 0));
  };

  const currentMedia = media[current];

  return (
    <div className="media-overlay">
      <div className="media-overlay-content">
        {currentMedia.type === "image" ? (
          <img src={currentMedia.url} alt={`Media ${current}`} />
        ) : currentMedia.type === "video" ? (
          <div
            className="video-placeholder-overlay"
            style={{ backgroundImage: `url(${currentMedia.thumbnailUrl})` }}
            onClick={(e) => {
              e.stopPropagation();
              if (currentMedia.originalUrl) {
                window.open(currentMedia.originalUrl, '_blank');
              }
            }}
          >
            <FontAwesomeIcon icon={faCirclePlay} style={{ color: "#ffffff" }} />
          </div>
        ) : null}
        <button
          className="media-overlay-arrow media-overlay-arrow-left"
          onClick={handlePrevious}
        >
          <FontAwesomeIcon icon={faChevronLeft} style={{ color: "#686e78" }} size="xl" />
        </button>
        <button
          className="media-overlay-arrow media-overlay-arrow-right"
          onClick={handleNext}
        >
          <FontAwesomeIcon icon={faChevronRight} style={{ color: "#686e78" }} size="xl" />
        </button>
        <button className="media-overlay-close-wrapper" onClick={onClose}>
          <span className="media-overlay-close-text">Close &nbsp;</span>
          <FontAwesomeIcon icon={faCircleXmark} style={{ color: "#f5f5f5" }} size="lg" />
        </button>
        {media.length > 1 && ( 
            <div className="carousel-counter">
              {current + 1} of {media.length}
            </div>
          )}
      </div>
    </div>
  );
};

interface MediaCardContainerProps {
  companyEnhancedFields: CompanyEnhancedFields;
}

const getYouTubeVideoId = (url: string): string | null => {
  const watchRegex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([a-zA-Z0-9_-]+)(?:&.*)?/;
  const shortsRegex = /(?:https?:\/\/)?(?:www\.)?youtube\.com\/shorts\/([a-zA-Z0-9_-]+)(?:&.*)?/;

  let match = url.match(watchRegex);
  if (match && match[1]) {
    return match[1];
  }
  match = url.match(shortsRegex);
  if (match && match[1]) {
    return match[1];
  }
  return null;
};

const getVimeoVideoId = (url: string): string | null => {
  const match = url.match(/(?:vimeo\.com\/(?:channels\/(?:\w+\/)?|groups\/(?:[^\/]*)\/videos\/|album\/\d+\/video\/|video\/|)(\d+)(?:[?&].*)?)/i);
  return match && match[1] ? match[1] : null;
};

const getThumbnailUrl = (url: string): string | null => {
  const youtubeId = getYouTubeVideoId(url);
  if (youtubeId) {
    return `https://img.youtube.com/vi/${youtubeId}/mqdefault.jpg`;
  }
  const vimeoId = getVimeoVideoId(url);
  if (vimeoId) {
    return `https://vumbnail.com/${vimeoId}.jpg`;
  }
  return null;
};


const MediaCardContainer: React.FC<MediaCardContainerProps> = ({
  companyEnhancedFields,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showOverlay, setShowOverlay] = useState(false);
  const [overlayIndex, setOverlayIndex] = useState(0);
  const [selectedMobileMediaIndex, setSelectedMobileMediaIndex] = useState(0);
  const [inputValue, setInputValue] = useState("1");
  const itemsPerPage = 6;

  const media = [
    ...(companyEnhancedFields.galleryPhotoUrls || [])
      .filter((url) => url)
      .map((url) => ({ url, type: "image" })),
    ...(companyEnhancedFields.videoUrls || [])
      .filter((url) => url)
      .map((url) => {
        const thumbnailUrl = getThumbnailUrl(url);
        return { url: thumbnailUrl || '', type: "video", thumbnailUrl, originalUrl: url };
      }),
  ];

  const totalPages = Math.ceil(media.length / itemsPerPage);

  const handlePageChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    if (value === "") {
      return;
    }

    const page = parseInt(value, 10);
    if (!isNaN(page) && page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handlePageNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
      setInputValue((currentPage + 1).toString());
    }
  };

  const handlePagePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
      setInputValue((currentPage - 1).toString());
    }
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, media.length);
  const currentMedia = media.slice(startIndex, endIndex);

  const openOverlay = (index) => {
    setOverlayIndex(index);
    setShowOverlay(true);
  };

  const closeOverlay = () => {
    setShowOverlay(false);
  };

  return (
    <>
      {/* Desktop View */}
      <div className="media-gallery-desktop">
        <div className="media-grid">
          {currentMedia.map((item, index) => (
            <div
              key={index}
              className="media-item"
              onClick={() => openOverlay(startIndex + index)}
            >
              {item.type === "image" ? (
                <img src={item.url} alt={`Media ${index}`} />
              ) : item.type === "video" ? (
                <div className="video-placeholder" style={{ backgroundImage: `url(${item.thumbnailUrl})` }}>
                  <FontAwesomeIcon icon={faCirclePlay} style={{ color: "#ffffff" }} />
                </div>
              ) : null}
            </div>
          ))}
        </div>
        <div className="pagination">
          <p className="paraText">Page</p>
          <input
            type="text"
            value={inputValue}
            onChange={handlePageChange}
            placeholder="Page"
          />
          <p className="paraText">Of</p>
          <p className="paraText">{totalPages}</p>
          <button
            className="pagination-button"
            onClick={handlePagePrevious}
            disabled={currentPage === 1}
          >
            <FontAwesomeIcon icon={faChevronLeft} style={{ color: "#686e78" }} />
          </button>
          <button
            className="pagination-button"
            onClick={handlePageNext}
            disabled={currentPage === totalPages}
          >
            <FontAwesomeIcon icon={faChevronRight} style={{ color: "#686e78" }} />
          </button>
        </div>
        <div className="pagination">
          <p>
            Clicking on a video link will redirect you to a third party website not governed by the Dun & Bradstreet Privacy Policy. Dun & Bradstreet disclaims any liability for information made available on or through these links.
          </p>
        </div>
      </div>

      {/* Mobile View Carousel */}
      <div className="media-gallery-mobile">
        {/* Preview Div for Selected Media */}
        {media.length > 0 && (
          <div className="mobile-preview-container">
            <div
              className="mobile-preview-item"
              onClick={() => openOverlay(selectedMobileMediaIndex)}
            >
              {media[selectedMobileMediaIndex].type === "image" ? (
                <img
                  src={media[selectedMobileMediaIndex].url}
                  alt="Selected Media Preview"
                  className="mobile-preview-image"
                />
              ) : media[selectedMobileMediaIndex].type === "video" ? (
                <div
                  className="video-placeholder mobile-preview-video"
                  style={{ backgroundImage: `url(${media[selectedMobileMediaIndex].thumbnailUrl})` }}
                >
                  <FontAwesomeIcon icon={faCirclePlay} style={{ color: "#ffffff" }} />
                </div>
              ) : null}
            </div>
          </div>
        )}

        {/* Carousel Thumbnails */}
        <div className="gallery-carousel-container">
          <div className="thumb-scroll">
            <div className="thumb-scroll-content">
              {media.map((item, index) => (
                <div
                  key={index}
                  className={`profile-gallery-item ${index === selectedMobileMediaIndex ? 'selected' : ''}`}
                  onClick={() => setSelectedMobileMediaIndex(index)}
                >
                  {item.type === "image" ? (
                    <img src={item.url} alt={`Gallery Thumbnail ${index}`} />
                  ) : item.type === "video" ? (
                    <div className="video-placeholder" style={{ backgroundImage: `url(${item.thumbnailUrl})` }}>
                      <FontAwesomeIcon icon={faCirclePlay} style={{ color: "#ffffff" }} />
                    </div>
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="pagination">
          <p>
            Clicking on a video link will redirect you to a third party website not governed by the Dun & Bradstreet Privacy Policy. Dun & Bradstreet disclaims any liability for information made available on or through these links.
          </p>
        </div>
      </div>
      {showOverlay && (
        <MediaOverlay
          media={media}
          currentIndex={overlayIndex}
          onClose={closeOverlay}
        />
      )}
    </>
  );
};

export default MediaCardContainer;
